/**
 * Shared brand registry for multi-brand ingress attribution.
 *
 * Brand is an entry/channel attribution dimension only. Nadoutong and
 * Nxtpath share the same account/org/API-key realm; exact host/origin
 * matching here decides which brand to stamp on new rows.
 */

export const BRAND_IDS = ['nadoutong', 'nxtpath'] as const;
export type BrandId = (typeof BRAND_IDS)[number];

export const DEFAULT_BRAND_ID: BrandId = 'nadoutong';

/**
 * The single brand every CUSTOMER-FACING BULK mail goes out as (#1935).
 *
 * Balance alerts and operator notices are announcements from one product, and
 * the business decided that product is Nxtpath regardless of which brand the
 * recipient signed up under. So the bulk sender, and the `{{ brand }}` word in
 * the copy, are this constant rather than a function of `accounts.signup_brand`.
 *
 * ── WHAT THIS DOES NOT PIN ──────────────────────────────────────────────────
 * The AUTHENTICATION channel — verification links, password resets, login
 * codes, org invitations — is still per brand, and must stay that way: those
 * mails answer a customer who is looking at that brand's console right now, and
 * their deliverability is why the codes arrive at all. `email-branding.ts` and
 * `bulk-email-branding.ts` are separate modules for that reason.
 *
 * Brand as a RECORD is also untouched: `accounts.signup_brand`,
 * `email_notice_recipients.brand`, the alert history's `brand` column and
 * `usage_logs.ingress_brand` all keep saying which brand a customer belongs to.
 * This constant answers a different question — whose name is on the envelope.
 */
export const BULK_EMAIL_BRAND: BrandId = 'nxtpath';

export type BrandEnvironment = 'dev' | 'staging' | 'production';

export function normalizeBrandId(raw: unknown): BrandId | null {
  return raw === 'nadoutong' || raw === 'nxtpath' ? raw : null;
}

export function brandOrDefault(raw: unknown): BrandId {
  return normalizeBrandId(raw) ?? DEFAULT_BRAND_ID;
}

/**
 * Additive raw/folded brand classification (#1098 deferred item 19,
 * landed with the brand-dimension pricing work in #1102).
 *
 * `brandOrDefault` silently folds out-of-registry historical brand
 * values to `nadoutong` on read — acceptable for display attribution,
 * but NOT for surfaces where brand is a pricing key (sales costs,
 * epic #1032): folding there would resolve a legacy brand to the
 * wrong brand's cost. Consumers that price by brand must either use
 * `normalizeBrandId` fail-closed (reject) or carry this classification
 * through the contract chain so a folded value stays visible.
 */
export interface BrandClassification {
  /** Registry brand after folding (`nadoutong` for unknown values). */
  readonly brand: BrandId;
  /** True when `raw` was NOT a registry brand and had to be folded. */
  readonly folded: boolean;
  /** The original raw string (null for non-string/empty input). */
  readonly raw: string | null;
}

export function classifyBrand(raw: unknown): BrandClassification {
  const normalized = normalizeBrandId(raw);
  if (normalized !== null) {
    return { brand: normalized, folded: false, raw: normalized };
  }
  return {
    brand: DEFAULT_BRAND_ID,
    folded: true,
    raw: typeof raw === 'string' && raw.length > 0 ? raw : null,
  };
}

export function normalizeBrandEnvironment(raw: unknown): BrandEnvironment {
  if (raw === 'production' || raw === 'staging') return raw;
  return 'dev';
}

export function brandApiHost(brand: BrandId, environment: BrandEnvironment): string {
  if (brand === 'nxtpath') {
    return environment === 'production'
      ? 'api.nxtpath.ai'
      : `api.${environment}.nxtpath.ai`;
  }
  return environment === 'production'
    ? 'api.nadoutong.org'
    : `api.${environment}.nadoutong.org`;
}

export function brandApiOrigin(brand: BrandId, environment: BrandEnvironment): string {
  return `https://${brandApiHost(brand, environment)}`;
}

/**
 * The customer-facing PLATFORM API host for a brand — where the console and
 * native clients post account calls, as opposed to `brandApiHost` (SDK) and
 * `brandConsoleHost` (browser UI).
 */
export function brandPlatformHost(brand: BrandId, environment: BrandEnvironment): string {
  if (brand === 'nxtpath') {
    return environment === 'production'
      ? 'platform.nxtpath.ai'
      : `platform.${environment}.nxtpath.ai`;
  }
  return environment === 'production'
    ? 'platform.nadoutong.org'
    : `platform.${environment}.nadoutong.org`;
}

export function resolveBrandFromPlatformHost(rawHost: string | null | undefined): BrandId | null {
  const host = normalizeHost(rawHost);
  if (host === null) return null;
  for (const environment of ['dev', 'staging', 'production'] as const) {
    for (const brand of BRAND_IDS) {
      if (host === brandPlatformHost(brand, environment)) {
        return brand;
      }
    }
  }
  return null;
}

/**
 * The customer-facing CONSOLE host for a brand — where a human goes, as
 * opposed to `brandApiHost`, which is where an SDK posts.
 *
 * Needed by senders that have no request to read an origin from (cron mail),
 * where `resolveConsoleWebOrigin`'s `PUBLIC_CONSOLE_WEB_ORIGIN` fallback is
 * brand-blind and would hand an Nxtpath customer a Nadoutong link. Only the
 * canonical `app.*` hosts are produced: the `*.pages.dev` rows in
 * `CONSOLE_ORIGIN_BRANDS` are additional accepted aliases, not addresses to
 * put in front of a customer. `brands.test.ts` pins the direction that must
 * hold — every origin this returns resolves back to the brand it was asked
 * for.
 */
export function brandConsoleHost(brand: BrandId, environment: BrandEnvironment): string {
  if (brand === 'nxtpath') {
    return environment === 'production'
      ? 'app.nxtpath.ai'
      : `app.${environment}.nxtpath.ai`;
  }
  return environment === 'production'
    ? 'app.nadoutong.org'
    : `app.${environment}.nadoutong.org`;
}

export function brandConsoleOrigin(brand: BrandId, environment: BrandEnvironment): string {
  return `https://${brandConsoleHost(brand, environment)}`;
}

export function resolveBrandFromApiHost(rawHost: string | null | undefined): BrandId | null {
  const host = normalizeHost(rawHost);
  if (host === null) return null;
  for (const environment of ['dev', 'staging', 'production'] as const) {
    for (const brand of BRAND_IDS) {
      if (host === brandApiHost(brand, environment)) {
        return brand;
      }
    }
  }
  return API_HOST_BRAND_ALIASES[host] ?? null;
}

/**
 * Additional accepted API ingress hosts beyond the canonical
 * `brandApiHost` grid — aliases to recognise, not addresses to hand out
 * (same split as `CONSOLE_ORIGIN_BRANDS` vs `brandConsoleHost`).
 *
 * `api.nxtpath.online` is the ICP-registered domestic production ingress
 * (multiregion P1, #1967): the mainland entry point for the cn region, still
 * the Nxtpath product. The dev/staging rows follow the workspace's
 * `api.<env>.<domain>` convention so a pre-production cn ingress resolves the
 * moment DNS exists, instead of silently stamping the default brand.
 */
const API_HOST_BRAND_ALIASES: Record<string, BrandId> = {
  'api.nxtpath.online': 'nxtpath',
  'api.dev.nxtpath.online': 'nxtpath',
  'api.staging.nxtpath.online': 'nxtpath',
};

export function resolveBrandFromConsoleOrigin(rawOrigin: string | null | undefined): BrandId | null {
  const origin = normalizeOrigin(rawOrigin);
  if (origin === null) return null;
  return CONSOLE_ORIGIN_BRANDS[origin] ?? null;
}

export function resolveBrandFromConsoleHost(rawHost: string | null | undefined): BrandId | null {
  const host = normalizeHost(rawHost);
  if (host === null) return null;
  return resolveBrandFromConsoleOrigin(`https://${host}`);
}

const CONSOLE_ORIGIN_BRANDS: Record<string, BrandId> = {
  'https://app.nadoutong.org': 'nadoutong',
  'https://app.dev.nadoutong.org': 'nadoutong',
  'https://app.staging.nadoutong.org': 'nadoutong',
  'https://nxtpath-console-web-dev.pages.dev': 'nxtpath',
  'https://nxtpath-console-web-staging.pages.dev': 'nxtpath',
  'https://nxtpath-console-web-production.pages.dev': 'nxtpath',
  'https://app.nxtpath.ai': 'nxtpath',
  'https://app.dev.nxtpath.ai': 'nxtpath',
  'https://app.staging.nxtpath.ai': 'nxtpath',
  // Tenant console repo split (#1612). This map is inert on its own: an origin
  // only reaches it after `PUBLIC_CONSOLE_WEB_EXTRA_ORIGINS` already allowed it,
  // so the production rows below are deliberately dormant until that env opens.
  // Landing them ahead of the env is the safe order — env-first would stamp
  // Nxtpath signups with the default `nadoutong` brand into `accounts.signup_brand`
  // and `orgs.primary_brand`, a billing dimension only a migration could repair.
  'https://tenant-nadoutong-console-web-dev.pages.dev': 'nadoutong',
  'https://tenant-nadoutong-console-web-staging.pages.dev': 'nadoutong',
  'https://tenant-nadoutong-console-web-production.pages.dev': 'nadoutong',
  'https://tenant-nxtpath-console-web-dev.pages.dev': 'nxtpath',
  'https://tenant-nxtpath-console-web-staging.pages.dev': 'nxtpath',
  'https://tenant-nxtpath-console-web-production.pages.dev': 'nxtpath',
  // Promoter portal (#2469 A2b-1 / tenant#193). Same landing-ahead-of-env
  // rule as the tenant console rows: an origin only reaches this map after
  // `PUBLIC_CONSOLE_WEB_EXTRA_ORIGINS` already allowed it.
  'https://tenant-nxtpath-promoter-web-dev.pages.dev': 'nxtpath',
  'https://tenant-nxtpath-promoter-web-staging.pages.dev': 'nxtpath',
  'https://tenant-nxtpath-promoter-web-production.pages.dev': 'nxtpath',
};

function normalizeHost(rawHost: string | null | undefined): string | null {
  if (typeof rawHost !== 'string') return null;
  const trimmed = rawHost.trim().toLowerCase();
  if (trimmed.length === 0 || trimmed.includes('*')) return null;
  const withoutPort = trimmed.startsWith('[')
    ? trimmed
    : trimmed.split(':')[0] ?? '';
  return withoutPort.length > 0 ? withoutPort : null;
}

function normalizeOrigin(rawOrigin: string | null | undefined): string | null {
  if (typeof rawOrigin !== 'string') return null;
  const trimmed = rawOrigin.trim();
  if (trimmed.length === 0 || trimmed.includes('*')) return null;
  const origin = (trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed).toLowerCase();
  return /^https:\/\/[a-z0-9.-]+(?::[0-9]+)?$/.test(origin) ? origin : null;
}
